import { useRef, useSyncExternalStore } from 'react';
import { ID, LineItem } from 'types';
import { useLotData } from '@hooks/useLotData';
import { SuggestedPriceInfoDrawer } from '@features/suggested-price-column/components/suggested-price-info-drawer/suggested-price-info-drawer';
import { suggestedPriceDrawerStore } from '@features/suggested-price-column/store/suggested-price-drawer-store';

interface SuggestedPriceInfoDrawerHostProps {
  currency: string;
  eventId: ID;
  lotId: ID;
  onResetOverride: (lineItemId: ID) => void;
  pricePrecision: number;
}

/**
 * The single "Why this price?" drawer for one lot's grid.
 *
 * Mount this beside the grid, not inside a cell. Handsontable mounts the cell renderer once per
 * visible cell, so owning the drawer there put 100+ of them in the tree at once; the cell now just
 * records which line item was clicked in `suggestedPriceDrawerStore` and this resolves it.
 *
 * Subscribing here rather than in LineItemsTable is deliberate: opening the drawer re-renders only
 * this component, leaving the memoized grid untouched. Everything the drawer shows is derived from
 * the line item in the merged `lots` view, so an unsaved override is reflected the same as a saved
 * one.
 *
 * The closed drawer stays mounted on the last line item it showed, because unmounting it the
 * instant the selection clears would cut off the slide-out animation.
 */
export const SuggestedPriceInfoDrawerHost = ({
  currency,
  eventId,
  lotId,
  onResetOverride,
  pricePrecision,
}: SuggestedPriceInfoDrawerHostProps) => {
  const { lot } = useLotData(lotId);
  const selection = useSyncExternalStore(
    suggestedPriceDrawerStore.subscribe,
    suggestedPriceDrawerStore.getSelection,
    suggestedPriceDrawerStore.getSelection,
  );

  const isForThisLot = selection != null && String(selection.lotId) === String(lotId);
  const lineItem: LineItem | undefined = isForThisLot
    ? lot?.line_items?.find((item: LineItem): boolean => String(item.id) === String(selection.lineItemId))
    : undefined;

  const lastShownRef = useRef<LineItem | undefined>(undefined);
  if (lineItem) lastShownRef.current = lineItem;
  const shown = lineItem ?? lastShownRef.current;

  if (!shown) return null;

  const predictive = shown.predictive_unit_price != null ? Number(shown.predictive_unit_price) : null;
  const negotiationStartingPrice = Number(shown.negotiation_starting_price ?? 0);
  const hasOverride = shown.suggested_price_override != null;

  return (
    <SuggestedPriceInfoDrawer
      aiValue={predictive}
      currency={currency}
      eventId={eventId}
      hasOverride={hasOverride}
      itemName={shown.name}
      lineItemId={shown.id}
      lotId={lotId}
      negotiationStartingPrice={negotiationStartingPrice > 0 ? negotiationStartingPrice : null}
      onClose={suggestedPriceDrawerStore.close}
      onReset={() => onResetOverride(shown.id)}
      open={Boolean(lineItem)}
      overrideValue={hasOverride ? Number(shown.suggested_price_override) : null}
      pricePrecision={pricePrecision}
    />
  );
};
